import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  MenuBook as BookIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { aiBooksApi, AiBook } from '../services/api';
import { renderError } from '../utils/errorUtils';

function formatBytes(n?: number | null) {
  const size = Number(n) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

const AIBooksPanel: React.FC = () => {
  const [books, setBooks] = useState<AiBook[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const loadBooks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await aiBooksApi.list();
      setBooks(res.data.books || []);
    } catch (err: any) {
      setError(renderError(err.response?.data?.details || err.response?.data?.error || 'Failed to load books'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBooks();
  }, [loadBooks]);

  const handleUpload = async () => {
    if (!file) {
      setError('Choose a book file first');
      return;
    }
    try {
      setUploading(true);
      setError(null);
      setSuccess(null);
      await aiBooksApi.upload({
        file,
        title: title.trim() || undefined,
        description: description.trim() || undefined,
      });
      setSuccess('Book uploaded');
      setFile(null);
      setTitle('');
      setDescription('');
      await loadBooks();
    } catch (err: any) {
      setError(renderError(err.response?.data?.details || err.response?.data?.error || 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (bookId: number) => {
    try {
      setError(null);
      await aiBooksApi.remove(bookId);
      setSuccess('Book deleted');
      await loadBooks();
    } catch (err: any) {
      setError(renderError(err.response?.data?.details || err.response?.data?.error || 'Delete failed'));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BookIcon color="primary" />
        Programming books
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload PDF or text/source files. Text and code files are indexed for the Improve Code tab.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Stack spacing={2} sx={{ mb: 3, maxWidth: 640 }}>
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          size="small"
          placeholder="Defaults to filename"
        />
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={2}
        />
        <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
          {file ? file.name : 'Choose file'}
          <input
            hidden
            type="file"
            accept=".pdf,.txt,.md,.markdown,.js,.jsx,.ts,.tsx,.py,.json,.html,.css,.sql,.sh,.c,.cpp,.h,.java,.go,.rs,text/*,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!file || uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
          >
            Upload book
          </Button>
          <Button startIcon={<RefreshIcon />} onClick={loadBooks} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <List>
        {books.map((book) => (
          <ListItem key={book.book_id} divider alignItems="flex-start">
            <ListItemText
              primary={book.title}
              secondary={
                <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" component="span">
                    {book.original_filename} · {formatBytes(book.file_size)}
                    {book.description ? ` · ${book.description}` : ''}
                  </Typography>
                  <Box sx={{ mt: 0.5, display: 'flex', gap: 1 }}>
                    <Chip
                      size="small"
                      label={Number(book.has_text) ? 'Indexed text' : 'File only'}
                      color={Number(book.has_text) ? 'success' : 'default'}
                    />
                  </Box>
                </Box>
              }
            />
            <ListItemSecondaryAction>
              <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(book.book_id)}>
                <DeleteIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
        {!loading && books.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No books yet. Upload one to use it in Improve Code.
          </Typography>
        )}
      </List>
    </Box>
  );
};

export default AIBooksPanel;
